import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { productCardMediaFilter } from './product-card-media-filter.js';
import { ProductCardServices } from './product-card-services.js';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  #fetchProductPageHandler = () => {
    if (!this.refs.quickAdd?.cachedProductHtml) {
      this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
    }
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Free bandwidth for the real PDP navigation (abort hover quick-add downloads).
    try {
      if (typeof window.__pzAbortQuickAddFetches === 'function') {
        window.__pzAbortQuickAddFetches();
      }
    } catch {
      /* ignore */
    }

    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) {
      console.warn('[product-card] Product card link not found — skipping card init');
      return;
    }
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);
    const gallery = this.#getCardGallery();
    gallery?.addEventListener('click', this.#handleGalleryClick, { capture: true });
    gallery?.addEventListener('click', this.#handleGalleryArrowClick, { capture: true });
    gallery?.addEventListener('pointerdown', this.#handleGalleryControlPointerDown, { capture: true });
    this.#setupGalleryHover(gallery);

    this.#ensureMediaFilterReady();

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.#getSlideshow()?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  /** Desktop-only hover on media; arrows/pagination restore carousel image. */
  #setupGalleryHover(gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    if (gallery.dataset.hoverSecondImage !== 'true') return;

    // mouseenter on the gallery fires when the pointer enters the gallery from outside
    // (including when the first hit-target is a child img). Do not require event.target
    // to be a slide — that check never passed when target was the gallery itself.
    gallery.addEventListener('mouseenter', this.#handleGalleryMouseEnter);
    gallery.addEventListener('mouseleave', this.#handleGalleryMouseLeave);

    gallery.querySelectorAll('slideshow-arrows, slideshow-controls').forEach((control) => {
      control.addEventListener('mouseenter', this.#clearHoverPreviewState);
    });
  }

  #handleGalleryMouseEnter = (event) => {
    if (!isDesktopBreakpoint()) return;
    if (
      window.matchMedia('(hover: none)').matches ||
      window.matchMedia('(pointer: coarse)').matches ||
      !window.matchMedia('(pointer: fine)').matches
    ) {
      return;
    }
    if (!(event.target instanceof Element)) return;
    if (
      event.target.closest(
        'slideshow-arrows, slideshow-controls, .slideshow-control, button, input, label, product-swatches, swatches-variant-picker-component'
      )
    ) {
      return;
    }
    // Force mouse pointerType — previewImage gates on it.
    this.previewImage(/** @type {PointerEvent} */ ({ pointerType: 'mouse', target: event.target }));
  };

  #handleGalleryMouseLeave = (event) => {
    if (!isDesktopBreakpoint()) return;
    const gallery = this.#getCardGallery();
    if (!gallery) return;

    const related = event.relatedTarget;
    if (related instanceof Node && gallery.contains(related)) {
      if (
        related instanceof Element &&
        related.closest('slideshow-arrows, slideshow-controls, .slideshow-control')
      ) {
        this.#clearHoverPreviewState();
      }
      return;
    }

    this.resetImage();
  };

  /** @returns {HTMLElement | null} */
  #getCardGallery() {
    return this.refs.cardGallery || this.querySelector('.card-gallery');
  }

  /** Slideshow lives inside card-gallery (child custom element), not in ProductCard refs. */
  #getSlideshow() {
    const cardGallery = this.#getCardGallery();
    return cardGallery?.querySelector('slideshow-component') ?? this.refs.slideshow ?? null;
  }

  #ensureMediaFilterReady() {
    const cardGallery = this.#getCardGallery();
    const slideshow = this.#getSlideshow();
    if (!cardGallery || !slideshow) return;
    if (productCardMediaFilter.getFilterFunction(cardGallery)) return;

    const onCollection = Boolean(document.querySelector('.main-collection-grid'));
    const isPriority =
      this.closest('[data-is-first-batch]')?.getAttribute('data-is-first-batch') === 'true';

    if (onCollection && !isPriority) {
      const run = () => this.#ensureMediaFilterReadyNow();
      if (window.requestIdleCallback) {
        requestIdleCallback(run, { timeout: 1200 });
      } else {
        setTimeout(run, 32);
      }
      return;
    }

    this.#ensureMediaFilterReadyNow();
  }

  #ensureMediaFilterReadyNow() {
    const cardGallery = this.#getCardGallery();
    const slideshow = this.#getSlideshow();
    if (!cardGallery || !slideshow) return;
    if (productCardMediaFilter.getFilterFunction(cardGallery)) return;
    ProductCardServices.syncGalleryNavMode(cardGallery);
    productCardMediaFilter.init(cardGallery, this, slideshow, this.variantPicker);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.removeEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.removeEventListener('change', this.#handleQuickAdd);
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);
    const gallery = this.#getCardGallery();
    gallery?.removeEventListener('click', this.#handleGalleryClick, { capture: true });
    gallery?.removeEventListener('click', this.#handleGalleryArrowClick, { capture: true });
    gallery?.removeEventListener('pointerdown', this.#handleGalleryControlPointerDown, {
      capture: true,
    });
    gallery?.removeEventListener('mouseenter', this.#handleGalleryMouseEnter);
    gallery?.removeEventListener('mouseleave', this.#handleGalleryMouseLeave);
    gallery?.querySelectorAll('slideshow-arrows, slideshow-controls').forEach((control) => {
      control.removeEventListener('mouseenter', this.#clearHoverPreviewState);
    });
  }

  #handleGalleryControlPointerDown = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('slideshow-arrows, slideshow-controls, .slideshow-control')) return;
    // Let #handleGalleryArrowClick own navigation — only clear hover preview here.
    const cardGallery = this.#getCardGallery();
    if (cardGallery?.dataset.hoverPreviewActive === 'true' || this.#hoverSlideShown) {
      ProductCardServices.clearHoverPreview(cardGallery);
      this.#hoverSlideShown = null;
      this.resetVariant.cancel();
    }
  };

  /**
   * Desktop arrow / pagination clicks — must run above quick-add overlay (in-stock cards).
   * @param {MouseEvent} event
   */
  #handleGalleryArrowClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const cardGallery = this.#getCardGallery();
    if (!cardGallery?.classList.contains('card-gallery--arrow-nav')) return;

    const lineControl = event.target.closest('.slideshow-control--line');
    const arrowControl = event.target.closest('.slideshow-control--prev, .slideshow-control--next');
    const control = arrowControl || lineControl;
    if (!control || !cardGallery.contains(control)) return;

    event.preventDefault();
    event.stopPropagation();

    const slideshow = this.#getSlideshow();
    if (!slideshow) return;

    ProductCardServices.clearHoverPreview(cardGallery);
    this.#hoverSlideShown = null;
    this.resetVariant.cancel();

    if (arrowControl) {
      const direction = arrowControl.classList.contains('slideshow-control--next') ? 'next' : 'previous';
      ProductCardServices.navigateCarousel(cardGallery, slideshow, direction);
      return;
    }

    const linesRoot = lineControl.closest('.slideshow-controls__lines');
    const lineItem = lineControl.closest('li');
    if (!linesRoot || !lineItem) return;

    const visibleItems = Array.from(linesRoot.querySelectorAll(':scope > li')).filter(
      (item) => !item.hidden && item.style.display !== 'none'
    );
    const index = visibleItems.indexOf(lineItem);
    if (index < 0) return;

    const navigable = ProductCardServices.getNavigableSlides(cardGallery);
    const target = navigable[index];
    if (!(target instanceof HTMLElement)) return;

    const slideId = target.getAttribute('slide-id') || target.getAttribute('data-media-id');
    const selectFn = slideshow._originalSelect?.bind(slideshow) || slideshow.select.bind(slideshow);
    if (slideId) {
      selectFn({ id: slideId }, undefined, { animate: false });
    }
    requestAnimationFrame(() => ProductCardServices.enforceSwipeLimit(cardGallery));
  };

  #clearHoverPreviewState() {
    const cardGallery = this.#getCardGallery();
    const wasHovering =
      Boolean(this.#hoverSlideShown) || cardGallery?.dataset.hoverPreviewActive === 'true';

    if (cardGallery) {
      ProductCardServices.clearHoverPreview(cardGallery);
    }
    if (this.#hoverSlideShown) {
      this.#hoverSlideShown = null;
    }
    this.resetVariant.cancel();

    if (wasHovering && cardGallery) {
      const slideshow = this.#getSlideshow();
      ProductCardServices.enforceSwipeLimit(cardGallery);
      const navigable = ProductCardServices.getNavigableSlides(cardGallery);
      const first = navigable[0];
      if (first instanceof HTMLElement) {
        ProductCardServices.paintCardGallerySlide(cardGallery, slideshow, first, navigable);
      }
    }
  }

  /** @param {Event} event */
  #shouldNavigateFromGallery(event) {
    if (!(event.target instanceof Element)) return false;
    if (event.defaultPrevented) return false;

    const blocked = event.target.closest(
      'button, input, label, select, a, slideshow-arrows, slideshow-controls, .slideshow-control, .quick-add__button, product-swatches, swatches-variant-picker-component'
    );
    if (blocked) return false;

    const slideshow = this.#getSlideshow();
    if (slideshow?.hasAttribute('dragging')) return false;

    return true;
  }

  #handleGalleryClick = (event) => {
    if (!this.#shouldNavigateFromGallery(event)) return;

    const link = this.refs.productCardLink;
    if (!link?.href) return;

    event.preventDefault();
    event.stopPropagation();
    this.#navigateToURL(event, new URL(link.href));
  };

  #handleGalleryPointerUp = (event) => {
    if (!this.#shouldNavigateFromGallery(event)) return;
    if (event.button !== 0) return;

    const link = this.refs.productCardLink;
    if (!link?.href) return;
    event.preventDefault();
    this.#navigateToURL(event, new URL(link.href));
  };

  #preloadNextPreviewImage() {
    const slideshow = this.#getSlideshow();
    const currentSlide = slideshow?.slides?.[slideshow?.current];
    const nextSlide = currentSlide?.nextElementSibling;
    const nextImage = nextSlide?.querySelector('img[loading="lazy"]');
    
    if (nextImage) {
      // Preload the image to prevent flicker on hover
      if (nextImage.dataset.src) {
        // If using data-src (lazy loading), load it now
        nextImage.src = nextImage.dataset.src;
        nextImage.removeAttribute('data-src');
      }
      
      // Remove lazy loading attribute and ensure image is loaded
      nextImage.removeAttribute('loading');
      
      // Force image load if not already loaded
      if (!nextImage.complete) {
        const img = new Image();
        img.src = nextImage.src;
        img.srcset = nextImage.srcset;
        img.sizes = nextImage.sizes;
      }
    }
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    // UNIFIED: All cards now use centralized media filtering
    // No need to skip variant cards - they all work the same way

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);

    const quickAdd = this.refs.quickAdd;
    const hasVariantHtml = Boolean(event?.detail?.data?.html);
    if (quickAdd && typeof quickAdd.clearCache === 'function') {
      quickAdd.clearCache();
    }
    if (quickAdd && hasVariantHtml) {
      quickAdd.fetchProductPage(this.productPageUrl);
    }

    if (event.target !== this.variantPicker && event?.detail?.data?.html) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    const cardGallery = this.#getCardGallery();
    const colorValue =
      event?.detail?.data?.colorValue ||
      ProductCardServices.getColorValue(cardGallery) ||
      ProductCardServices.getColorValue(this);
    if (cardGallery && colorValue) {
      this.resetVariant.cancel();
      productCardMediaFilter.applyColor(cardGallery, colorValue);
    } else {
      this.#updateVariantImages(colorValue);
    }
    this.#previousSlideIndex = null;
  };

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    // Skip if event doesn't have HTML data (e.g., from swatch selection on variant cards)
    if (!event?.detail?.data?.html) {
      return;
    }
    
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      this.refs.productCardLink.href = anchorElement.href;
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    // Skip if event doesn't have HTML data (e.g., from swatch selection on variant cards)
    if (!event?.detail?.data?.html) {
      return;
    }
    
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Update variant images using centralized media filtering.
   * Works for ALL card types - uses same logic as variant cards.
   */
  /**
   * @param {string} [colorOverride] - From variant:update (e.g. collection swatch click)
   */
  #updateVariantImages(colorOverride = '') {
    const slideshow = this.#getSlideshow();
    const cardGallery = this.#getCardGallery();

    if (!cardGallery || !slideshow) {
      return;
    }

    const selectedOption = this.variantPicker?.selectedOption;
    const colorValue =
      colorOverride ||
      selectedOption?.value ||
      selectedOption?.dataset?.optionValue ||
      selectedOption?.textContent?.trim() ||
      '';

    if (!colorValue) {
      return;
    }

    const filterFunction = productCardMediaFilter.getFilterFunction(cardGallery);
    if (filterFunction) {
      filterFunction(colorValue);
    } else {
      productCardMediaFilter.applyColor(cardGallery, colorValue);
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /** @type {HTMLElement | null} */
  #hoverSlideShown = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    const cardGallery = this.#getCardGallery();
    if (cardGallery) {
      ProductCardServices.clearHoverPreview(cardGallery);
      requestAnimationFrame(() => ProductCardServices.enforceSwipeLimit(cardGallery));
    }

    if (event.detail?.userInitiated) {
      const navigable = cardGallery ? ProductCardServices.getNavigableSlides(cardGallery) : [];
      const allSlides = cardGallery?.querySelectorAll('slideshow-slide');
      const currentIndex = event.detail.index;
      const currentSlide = allSlides?.[currentIndex];
      if (currentSlide instanceof HTMLElement && navigable.includes(currentSlide)) {
        ProductCardServices.syncSwatchToSlideColor(cardGallery, currentSlide);
      }
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant color on desktop hover.
   * @param {string | number} mediaId - The media id of the hovered swatch.
   * @param {PointerEvent} [event] - The pointer event.
   */
  previewVariant(mediaId, event) {
    if (event instanceof PointerEvent && event.pointerType !== 'mouse') return;
    if (!isDesktopBreakpoint()) return;

    const slideshow = this.#getSlideshow();
    if (!slideshow) return;

    this.#clearHoverPreviewState();

    const label =
      event?.target instanceof Element
        ? event.target.closest('label.variant-option__button-label--has-swatch')
        : null;
    const input = label?.querySelector('input[type="radio"]');
    const colorValue = input?.value || '';

    const cardGallery = this.#getCardGallery();

    if (cardGallery && colorValue) {
      this.resetVariant.cancel();
      const filterFn = productCardMediaFilter.getFilterFunction(cardGallery);
      if (filterFn) {
        filterFn(colorValue);
      } else {
        const cardType = ProductCardServices.detectCardType(this);
        productCardMediaFilter.filterMediaByColor(cardGallery, slideshow, colorValue, cardType, {});
      }
    }
  }

  /**
   * Previews hover media on desktop pointer hover only (not click, not mobile).
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    const slideshow = this.#getSlideshow();

    if (!slideshow) return;
    // Allow synthetic enter events that force pointerType, and real mouse pointers.
    if (event?.pointerType && event.pointerType !== 'mouse') return;
    if (!isDesktopBreakpoint()) return;
    if (
      window.matchMedia('(hover: none)').matches ||
      window.matchMedia('(pointer: coarse)').matches ||
      !window.matchMedia('(pointer: fine)').matches
    ) {
      return;
    }

    if (event?.target instanceof Element) {
      if (
        event.target.closest(
          'slideshow-arrows, slideshow-controls, .slideshow-control, button, input, label, product-swatches'
        )
      ) {
        return;
      }
    }

    this.resetVariant.cancel();

    const cardGallery = this.#getCardGallery();
    const colorValue =
      ProductCardServices.getColorValue(cardGallery) ||
      ProductCardServices.getColorValue(this) ||
      this.closest?.('[data-collection-variant-color]')?.getAttribute('data-collection-variant-color') ||
      this.variantPicker?.selectedOption?.value ||
      '';

    if (!cardGallery || !colorValue) return;

    const slides = cardGallery.querySelectorAll('slideshow-slide');
    const hoverSlide = ProductCardServices.getHoverSlideForColor(slides, colorValue);
    if (!(hoverSlide instanceof HTMLElement)) return;

    this.#hoverSlideShown = hoverSlide;
    cardGallery.dataset.hoverPreviewActive = 'true';
    hoverSlide.hidden = false;
    hoverSlide.removeAttribute('hidden');
    hoverSlide.style.display = '';
    hoverSlide.style.opacity = '1';
    hoverSlide.style.visibility = 'visible';
    hoverSlide.setAttribute('reveal', '');
    hoverSlide.setAttribute('aria-hidden', 'false');

    // Hide other navigable slides so the hover image is what the user sees even if
    // slideshow.select is unavailable / overridden.
    slides.forEach((slide) => {
      if (!(slide instanceof HTMLElement) || slide === hoverSlide) return;
      const role = slide.getAttribute('data-media-role') || '';
      const position = parseInt(slide.getAttribute('data-media-position') || '999', 10);
      if (role === 'swatch' || position === ProductCardServices.CARD_HOVER_POSITION) return;
      if (slide.hidden || slide.hasAttribute('hidden')) return;
      slide.style.opacity = '0';
      slide.style.visibility = 'hidden';
    });

    const slideId = hoverSlide.getAttribute('slide-id') || hoverSlide.getAttribute('data-media-id');
    if (slideId) {
      const selectFn = slideshow._originalSelect?.bind(slideshow) || slideshow.select?.bind(slideshow);
      if (typeof selectFn === 'function') {
        selectFn({ id: slideId }, undefined, { animate: false, hoverPreview: true });
      }
    }
  }

  /**
   * Resets the image to the variant image.
   */
  resetImage() {
    const slideshow = this.#getSlideshow();
    const cardGallery = this.#getCardGallery();

    if (this.#hoverSlideShown || cardGallery?.dataset.hoverPreviewActive === 'true') {
      this.#clearHoverPreviewState();

      if (cardGallery) {
        ProductCardServices.enforceSwipeLimit(cardGallery);
        const navigable = ProductCardServices.getNavigableSlides(cardGallery);
        const first = navigable[0];
        if (first instanceof HTMLElement) {
          ProductCardServices.paintCardGallerySlide(cardGallery, slideshow, first, navigable);
          return;
        }
      }
    }

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const slideshow = this.#getSlideshow();

    if (!slideshow) return;

    const cardGallery = this.#getCardGallery();
    const manualSwatchFieldset = this.querySelector(
      'fieldset.variant-option--swatches[data-user-has-manually-selected="true"]'
    );
    if (manualSwatchFieldset) {
      return;
    }

    const colorValue =
      ProductCardServices.getColorValue(cardGallery) ||
      ProductCardServices.getColorValue(this) ||
      this.variantPicker?.selectedOption?.value ||
      '';

    if (cardGallery && colorValue) {
      const filterFn = productCardMediaFilter.getFilterFunction(cardGallery);
      if (filterFn) {
        filterFn(colorValue);
        return;
      }
      productCardMediaFilter.applyColor(cardGallery, colorValue);
      return;
    }

    const isVariantCard =
      this.hasAttribute('data-collection-variant-id') ||
      Boolean(this.closest('[data-collection-variant-id]'));
    if (isVariantCard) return;

    const defaultSlide = slideshow.defaultSlide;
    const slideId = defaultSlide?.getAttribute('slide-id');
    if (defaultSlide && slideshow.slides?.includes(defaultSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    } else if (!this.variantPicker?.selectedOption) {
      slideshow.previous(undefined, { animate: false });
      return;
    }

    const id = this.variantPicker.selectedOption.dataset.optionMediaId;
    if (!id) {
      slideshow.previous(undefined, { animate: false });
      return;
    }

    slideshow.select({ id }, undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Check for quick add buttons specifically
    const quickAddButton = event.target.closest('.quick-add__button');
    if (quickAddButton) {
      return; // Don't navigate for quick add buttons
    }

    // Check for slideshow navigation controls (arrows/chevrons/pagination)
    const slideshowControl = event.target.closest(
      'slideshow-arrows, slideshow-controls, .slideshow-control, button.slideshow-control'
    );
    if (slideshowControl) {
      return;
    }

    // Gallery media: navigate unless handled by dedicated gallery click listener
    const gallery = this.#getCardGallery();
    if (gallery && event.target instanceof Element && gallery.contains(event.target)) {
      if (this.#shouldNavigateFromGallery(event)) {
        const link = this.refs.productCardLink;
        if (link?.href) {
          event.preventDefault();
          event.stopPropagation();
          this.#navigateToURL(event, new URL(link.href));
        }
      }
      return;
    }

    const interactiveElement = event.target.closest('button, input, label, select, a, [tabindex="1"]');

    // If the click was on an interactive element which is not the main link, do nothing.
    if (interactiveElement && interactiveElement !== this.refs.productCardLink) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }
    history.replaceState({}, '', url.toString());

    this.#navigateToURL(event, linkURL);
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce((/** @type {PointerEvent} */ event) => {
    if (event?.pointerType && event.pointerType !== 'mouse') return;
    if (!isDesktopBreakpoint()) return;
    this.#resetVariant();
  }, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    if (!window.matchMedia('(min-width: 750px)').matches) return;

    this.querySelectorAll('.variant-option__swatch-list').forEach((list) => {
      list.classList.add('variant-option__swatch-list--show-all-desktop');
    });

    const moreControl = this.querySelector('.variant-option__swatch-more--desktop');
    if (moreControl instanceof HTMLElement) {
      moreControl.setAttribute('hidden', '');
    }

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
